import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCode, ErrorResponse, Schema } from '@api';
import { MAP_KEY_NAMES, transform } from '@shared/utils/transform';
import { Utils } from '@shared/utils/utils';

const BUSINESS_STATUSES = [400, 403, 404, 409, 422];

export type ErrorKind = 'network' | 'authentication' | 'business' | 'technical' | 'unknown';

export type FieldErrorCode = Schema<'FieldErrorCode'>;

export type ErrorParams = Schema<'ErrorParams'>;

export type AppFieldError = Schema<'FieldError'>;

export class AppError extends Error {
  readonly kind: ErrorKind;
  readonly status: number;
  readonly httpError: HttpErrorResponse;
  readonly code?: ErrorCode;
  readonly params?: ErrorParams;
  readonly fieldErrors?: AppFieldError[];

  constructor({
    kind,
    httpError,
    code,
    params,
    fieldErrors,
  }: {
    kind: ErrorKind;
    httpError: HttpErrorResponse;
    code?: ErrorCode;
    params?: ErrorParams;
    fieldErrors?: AppFieldError[];
  }) {
    super(`[API ${httpError.status}] ${code ?? kind}`);
    this.name = 'AppError';
    this.kind = kind;
    this.status = httpError.status;
    this.httpError = httpError;
    this.code = code;
    this.params = params;
    this.fieldErrors = fieldErrors;
  }

  static fromHttpError(httpError: HttpErrorResponse): AppError {
    const body = transform(httpError.error, Utils.toCamel, MAP_KEY_NAMES) as ErrorResponse | null | undefined;
    return new AppError({
      kind: AppError._getKind(httpError.status, body?.code),
      httpError,
      code: body?.code ?? undefined,
      params: body?.params ?? undefined,
      fieldErrors: body?.fieldErrors ?? undefined,
    });
  }

  private static _getKind(status: number, code: ErrorCode | null | undefined): ErrorKind {
    if (status === 0) return 'network';
    if (status === 401) return code === 'INVALID_CREDENTIALS' ? 'business' : 'authentication';
    if (BUSINESS_STATUSES.includes(status)) return 'business';
    if (status >= 500) return 'technical';
    return 'unknown';
  }
}

export class HandledAppError extends Error {
  constructor(readonly error: AppError) {
    super(error.message);
    this.name = 'HandledAppError';
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

export function isAppFieldError(error: unknown): error is AppError & { fieldErrors: AppFieldError[] } {
  return isAppError(error) && !!error.fieldErrors?.length;
}

/**
 * Transforms error parameters to translate parameters for the translate pipe or translate methods.
 *
 * Drops empty params and joins list params, so they can be interpolated in a message (e.g. `{{ allowedFormats }}`).
 *
 * Example:
 * `{ allowedFormats: ['pdf', 'png'], maxSizeMb: 10, minNumber: null }` → `{ allowedFormats: 'pdf, png', maxSizeMb: 10 }`
 */
export function toTranslateParams(params: ErrorParams | null | undefined): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params ?? {})
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([name, value]) => [name, Array.isArray(value) ? value.join(', ') : value]),
  ) as Record<string, string | number>;
}

export function isErrorHandled(error: unknown): error is HandledAppError {
  return error instanceof HandledAppError;
}
